import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AttorneysContent } from '../../api/LettersApi';

/**
 * Who signs the letters (Lloyd, Aug 17 2026: "laws may change or we add attorneys").
 *
 * Order is meaning here, not presentation: the first attorney is the one whose signature appears
 * over the line, and the rest are listed beneath as attorneys of record. That is what the letters'
 * "attorneys listed below" sentence points at, which is why the list is reorderable and why the
 * consequence of being first is spelled out on screen rather than left to be discovered.
 */

interface Props {
  content: AttorneysContent;
  readOnly: boolean;
  onChange: (content: AttorneysContent) => void;
}

const AttorneysEditor: React.FC<Props> = ({ content, readOnly, onChange }) => {
  const attorneys = content.attorneys ?? [];

  const update = (index: number, patch: Partial<AttorneysContent['attorneys'][number]>) =>
    onChange({
      attorneys: attorneys.map((attorney, i) =>
        i === index ? { ...attorney, ...patch } : attorney,
      ),
    });

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= attorneys.length) return;
    const next = [...attorneys];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    onChange({ attorneys: next });
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        The first attorney listed signs the letter — their name appears over the signature line.
        Everyone below is listed as an attorney of record. This block appears on all three letters.
      </Alert>

      <Stack spacing={2}>
        {attorneys.map((attorney, index) => (
          <Card key={index} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {index === 0 ? 'Signs the letter' : `Attorney of record ${index + 1}`}
                </Typography>
                {!readOnly && (
                  <Stack direction="row">
                    <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === attorneys.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={attorneys.length === 1}
                      onClick={() =>
                        onChange({ attorneys: attorneys.filter((_a, i) => i !== index) })
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name"
                  disabled={readOnly}
                  value={attorney.name}
                  onChange={(e) => update(index, { name: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Texas Bar No."
                  disabled={readOnly}
                  helperText="Number only — the letter prints the label"
                  value={attorney.barNumber}
                  onChange={(e) => update(index, { barNumber: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  disabled={readOnly}
                  value={attorney.title}
                  onChange={(e) => update(index, { title: e.target.value })}
                />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {!readOnly && (
        <Button
          sx={{ mt: 2 }}
          startIcon={<AddIcon />}
          onClick={() =>
            onChange({
              attorneys: [
                ...attorneys,
                { name: '', barNumber: '', title: 'Attorney at Law' },
              ],
            })
          }
        >
          Add attorney
        </Button>
      )}
    </Box>
  );
};

export default AttorneysEditor;
